import { UserState } from "@/context/common/general/initial-state.types";
import { Parm0Callback } from "@Components/app/members/teamInterfaces";
import SecurityCheckDialog from "@Components/auth/SecurityCheckDialog";
import { Actions, AppContext, Events } from "@Context/common/general/context";
import { AuthSecurityLevel } from "@Enums";
import { useContext, useEffect, useState } from "react";

export enum AuthErrorCode {
  AppDeleted = "auth/app-deleted",
  AppNotAuthorized = "auth/app-not-authorized",
  ArgumentError = "auth/argument-error",
  EmailAlreadyExists = "auth/email-already-exists",
  InvalidApiKey = "auth/invalid-api-key",
  InvalidUserToken = "auth/invalid-user-token",
  InvalidTenantId = "auth/invalid-tenant-id",
  NetworkRequestFailed = "auth/network-request-failed",
  OperationNotAllowed = "auth/operation-not-allowed",
  RequiresRecentLogin = "auth/requires-recent-login",
  TooManyRequests = "auth/too-many-requests",
  UnauthorizedDomain = "auth/unauthorized-domain",
  UserDisabled = "auth/user-disabled",
  UserNotFound = "auth/user-not-found",
  UserTokenExpired = "auth/user-token-expired",
  WebStorageUnsupported = "auth/web-storage-unsupported",
  WrongPassword = "auth/wrong-password",
  InvalidEmail = "auth/invalid-email",
  MissingEmail = "auth/missing-email",
}

const gracePeriodMins = 30;
const reAuthGracePeriod = gracePeriodMins * 60 * 1000;

// Quick hack to break stale state closure of checkReAuthRequired
let hoistedlastReAuthDTS: number | null = null;

interface IAuthService {
  onForgotPassword?: Parm0Callback;
}

const AuthService = ({ onForgotPassword = () => {} }: IAuthService) => {
  const { state, dispatch } = useContext(AppContext);
  const [reAuthRequired, setReAuthRequired] = useState<boolean>(false);
  const [reAuthSuccessCallback, setReAuthSuccessCallback] = useState<Parm0Callback>(() => {});
  const [reAuthCancelCallback, setReAuthCancelCallback] = useState<Parm0Callback>(() => {});

  useEffect(() => {
    // Hoisting hack to prevent stale state references on nested callbacks
    hoistedlastReAuthDTS = state.auth.lastAuthDTS;
  }, [state.auth.lastAuthDTS]);

  const onReAuthRequired = ({
    securityLevel = AuthSecurityLevel.L0,
    onSuccess,
    onCancel,
  }: {
    securityLevel?: AuthSecurityLevel;
    onSuccess?: () => void;
    onCancel?: () => void;
  }) => {
    if (onSuccess) setReAuthSuccessCallback(() => onSuccess);
    if (onCancel) setReAuthCancelCallback(() => onCancel);
    switch (securityLevel) {
      // LEVEL 0 always require reAuth
      case AuthSecurityLevel.L0:
        setReAuthRequired(true);
        break;
      // LEVEL 1 require reAuth if outside grace period
      case AuthSecurityLevel.L1:
        if (checkReAuthRequired()) {
          setReAuthRequired(true);
        } else {
          if (onSuccess) onSuccess();
          resetAuthCallbacks();
        }
        break;
      // LEVEL 2 + 3 no reAuth // Todo - make level 2 require pin entry
      case AuthSecurityLevel.L2:
      case AuthSecurityLevel.L3:
      default:
        if (onSuccess) onSuccess();
        resetAuthCallbacks();
        break;
    }
  };

  const onAccountInfoUpdated = (user: UserState) => {
    if (!user) return;
    dispatch({
      type: Actions.AuthActions.CheckUserHasPin,
      disableLoaders: true,
      payload: {
        user_id: user.id,
      },
    });
  };

  useEffect(() => {
    dispatch({ type: Actions.FirebaseActions.Auth });
    Events.auth.reAuthRequired.uniq(onReAuthRequired);
    Events.auth.accountInfoUpdated.uniq(onAccountInfoUpdated);

    return () => {
      Events.auth.reAuthRequired.remove(onReAuthRequired);
      Events.auth.accountInfoUpdated.remove(onAccountInfoUpdated);
    };
  }, []);

  const resetAuthCallbacks = () => {
    setReAuthSuccessCallback(() => {});
    setReAuthCancelCallback(() => {});
  };

  const checkReAuthRequired = () => {
    const now = new Date().getTime();
    return hoistedlastReAuthDTS ? now - hoistedlastReAuthDTS > reAuthGracePeriod : true;
  };

  const onConfirmPassword = () => {
    if (reAuthSuccessCallback) {
      reAuthSuccessCallback();
    }
    resetAuthCallbacks();
    setReAuthRequired(false);
  };

  const onCancelPassword = () => {
    if (reAuthCancelCallback) {
      reAuthCancelCallback();
    }
    resetAuthCallbacks();
    setReAuthRequired(false);
  };

  const onDismissPassword = () => {
    resetAuthCallbacks();
    setReAuthRequired(false);
  };

  return (
    <>
      {/* ReAuth Dialog */}
      <SecurityCheckDialog
        open={reAuthRequired}
        onConfirm={onConfirmPassword}
        onCancel={onCancelPassword}
        onDismiss={onDismissPassword}
        onForgotPassword={onForgotPassword}
      />
    </>
  );
};

export default AuthService;
